import React from 'react'
import * as yup from 'yup'
// Ui
import Box from 'ui/Box'
import Row from 'ui/Row'
import Col from 'ui/Col'
import Input from 'ui/Form/Input'
import AvatarPicker from 'ui/Form/AvatarPicker'
import PropTypes from 'prop-types'
// Helper
import { resolveErrorMessage } from 'helpers'
import { phone } from 'helpers/rules'
import { isValidExtension, isValidSize } from 'helpers/file'
// Use
import { useTablet } from 'use/media'
import useFormHook from 'use/form'
import useMounted from 'use/mounted'
import { useDispatch } from 'react-redux'
// Bootstrap
import { Form } from 'react-bootstrap'
// Api
import { updateLessor } from 'api/lessor'
import { updateProfileAvatar } from 'api/profile'
// Store
import { getUserProfile } from 'store/modules/auth'
// Services
import dialog from 'services/dialog'

const accept = '.jpg,.jpeg,.png'
const validationSchema = yup.object({
  lessorName: yup.string().trim().required('The full name field is required'),
  email: yup
    .string()
    .trim()
    .required('The email field is required')
    .email('Please input correct email address'),
  mailingAddress: yup
    .string()
    .trim()
    .required('The mailing address field is required'),
  mobiNo: yup
    .string()
    .trim()
    .required('The contact number field is required')
    .test('phone', 'Invalid contact number', phone),
  notificationSMS: yup.boolean(),
  avatar: yup
    .mixed()
    .test(
      'fileSize',
      'File too large',
      (value) => typeof value === 'string' || isValidSize(value, 3)
    )
    .test(
      'fileFormat',
      'Unsupported Format',
      (value) => typeof value === 'string' || isValidExtension(value, accept)
    ),
})

const LessorUpdateForm = ({ setEditMode, profile, onLessorUpdated }) => {
  const isTablet = useTablet()
  const dispatch = useDispatch()
  const isMounted = useMounted()
  const { form, errors, validate, onChange, onBlur } = useFormHook({
    initialForm: {
      email: profile.email,
      mailingAddress: profile.mailingAddress,
      mobiNo: profile.mobiNo,
      notificationSMS:
        profile.notificationSMS === null ? false : profile.notificationSMS,
      lessorName: profile.lessorName,
      companyId: profile.companyId,
      vendorId: profile.vendorId,
      avatar: profile.profilePhoto,
    },
    schema: validationSchema,
  })
  // Onchange radio
  const handleChangeRadio = (e) => {
    onChange(e.target.value === 'true', 'notificationSMS')
  }
  // Events
  const onSubmit = () => {
    dialog.confirm({
      title: 'Save changes?',
      message: `Changes made to your account will reflect within 24-48 hours. Are you sure you want to proceed?`,
      onConfirm: async ({ setLoading, setVisible: setConfirmVisible }) => {
        try {
          setLoading(true)
          const res = await updateLessor({
            email: form.email,
            mailingAddress: form.mailingAddress,
            mobiNo: form.mobiNo,
            notificationSMS: form.notificationSMS,
            lessorName: form.lessorName,
            companyId: form.companyId,
            vendorId: form.vendorId,
          })
          if (form.avatar instanceof File) {
            await updateProfileAvatar(form.avatar)
          }
          if (res.status === 200) {
            await dispatch(getUserProfile())
            await onLessorUpdated()
          }
          dialog.alert({
            title: 'Change saved!',
            message: `You’ve successfully saved the changes you’ve made to your account.
    Please wait 24-48 hours for these to reflect in Paytrack.`,
            onConfirm: ({ setVisible: setAlertVisible }) => {
              setAlertVisible(false)
            },
          })
        } catch (error) {
          dialog.alert({
            title: await resolveErrorMessage(error),
          })
        } finally {
          setLoading(false)
          isMounted.current && setEditMode(false)
          setConfirmVisible(false)
        }
      },
    })
  }

  const onSubmitClick = async (e) => {
    try {
      e.preventDefault()
      await validate()
      onSubmit()
    } catch (error) {
      //
    }
  }

  return (
    <Box
      style={{
        border: '1px solid #EBEEF5',
        borderRadius: '4px',
      }}
    >
      <form id="LessorUpdateForm" onSubmit={onSubmitClick}>
        <AvatarPicker
          accept={accept}
          name="avatar"
          value={form.avatar}
          errors={errors}
          onChange={onChange}
        />
        <Row>
          <Col sm={8} md={5}>
            <Input
              name="lessorName"
              value={form.lessorName}
              errors={errors}
              type="text"
              label="Full name"
              placeholder="Enter your name"
              disabled={true}
              onChange={onChange}
              onBlur={onBlur}
            />
          </Col>
          <Col md={{ span: 4, offset: 1 }} />
        </Row>
        <Row>
          <Col sm={8} md={5}>
            <Input
              name="email"
              value={form.email}
              errors={errors}
              type="text"
              label="Emaill address"
              placeholder="Enter your email address"
              onChange={onChange}
              onBlur={onBlur}
            />
          </Col>
          <Col sm={8} md={{ span: 5, offset: 1 }}>
            <Input
              name="mobiNo"
              value={form.mobiNo}
              errors={errors}
              type="text"
              label="Contact no."
              placeholder="+63 XXXXXXXXXX"
              onChange={onChange}
              onBlur={onBlur}
              phoneNumber={true}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={11}>
            <Input
              name="mailingAddress"
              value={form.mailingAddress}
              errors={errors}
              type="text"
              label="Mailing address"
              placeholder="Enter your address"
              textarea={!isTablet}
              onChange={onChange}
              onBlur={onBlur}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Group>
              <Form.Label>Allow SMS notif ?</Form.Label>
              <div>
                <Form.Check
                  inline
                  label="Yes"
                  name="notificationSMS"
                  type="radio"
                  value={true}
                  checked={form.notificationSMS}
                  onChange={handleChangeRadio}
                />
                <Form.Check
                  inline
                  label="No"
                  name="notificationSMS"
                  type="radio"
                  value={false}
                  checked={!form.notificationSMS}
                  onChange={handleChangeRadio}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
      </form>
    </Box>
  )
}
LessorUpdateForm.propTypes = {
  profile: PropTypes.object,
  setEditMode: PropTypes.func,
  onLessorUpdated: PropTypes.func,
}

LessorUpdateForm.defaultProps = {
  profile: {},
  setEditMode: () => {},
  onLessorUpdated: () => {},
}

export default LessorUpdateForm
